<template>
  <div class="de-broglie-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$λ = h / p$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>动量 p (mv)</label>
            <span class="val">{{ momentum.toFixed(2) }} <small>kg·m/s</small></span>
          </div>
          <div class="hud-item orange">
            <label>德布罗意波长 λ</label>
            <span class="val">{{ wavelength.toFixed(2) }} <small>pm</small></span>
          </div>
        </div>

        <svg width="100%" height="240" viewBox="0 0 400 240">
          <defs>
             <filter id="glow-wave"><feGaussianBlur stdDeviation="3" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
             <linearGradient id="wave-grad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" :stop-color="waveColor" stop-opacity="0.1" />
                <stop offset="50%" :stop-color="waveColor" stop-opacity="0.8" />
                <stop offset="100%" :stop-color="waveColor" stop-opacity="0.1" />
             </linearGradient>
          </defs>

          <!-- 概率密度背景 -->
          <rect width="400" height="240" fill="url(#wave-grad)" opacity="0.1" />

          <!-- 物质波形 (波包) -->
          <path :d="envelopePath" fill="rgba(255,255,255,0.03)" stroke="none" />
          <path :d="wavePath" fill="none" :stroke="waveColor" stroke-width="2.5" filter="url(#glow-wave)" />
          
          <!-- 粒子概率云 -->
          <g v-for="n in 5" :key="n">
             <circle :cx="particleX + (Math.sin(t*2+n)*10)" 
                     :cy="120 + (Math.cos(t*3+n)*5)" 
                     :r="2+n" 
                     :fill="waveColor" 
                     :opacity="0.6 - n*0.1" />
          </g>

          <!-- 核心粒子实体 -->
          <circle :cx="particleX" cy="120" r="4" fill="#fff" filter="url(#glow-wave)">
             <animate attributeName="r" values="3;5;3" dur="0.5s" repeatCount="indefinite" />
          </circle>

          <!-- 坐标参考 -->
          <line x1="0" y1="120" x2="400" y2="120" stroke="rgba(255,255,255,0.1)" stroke-dasharray="2,4" />
        </svg>

        <div class="wave-info-tag" :style="{ color: waveColor }">
           波动性特征：{{ wavelength > 20 ? '强 (显性)' : '弱 (准经典)' }}
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex flex-col gap-2">
              <div class="flex items-center gap-4">
                 <span class="label" style="width: 80px">粒子质量 m</span>
                 <el-slider v-model="mass" :min="1" :max="100" size="small" class="flex-1"></el-slider>
                 <span class="val-tag">{{ mass }} m_e</span>
              </div>
              <div class="flex items-center gap-4">
                 <span class="label" style="width: 80px">运动速度 v</span>
                 <el-slider v-model="velocity" :min="1" :max="100" size="small" class="flex-1"></el-slider>
                 <span class="val-tag">{{ velocity }} c/100</span>
              </div>
           </div>
        </div>

        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>物理规律：</strong>质量或速度（动量）越大，对应的德布罗意波长越短。对于宏观物体，λ 极小，波动性完全被掩盖；对于电子等微观粒子，λ 与原子间距相当，从而表现出衍射和干涉现象。物质的<strong>本质既是粒子也是波</strong>。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DeBroglieViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      mass: 10,
      velocity: 20,
      particleX: 200,
      t: 0,
      running: true
    }
  },
  computed: {
    momentum() { return this.mass * this.velocity; },
    wavelength() { return 500 / (this.momentum || 1); }, 
    waveColor() {
       // High momentum (Short λ) is Violet, Low is Red
       const hue = Math.max(0, Math.min(240, this.momentum * 0.1));
       return `hsl(${240 - hue}, 80%, 60%)`;
    },
    wavePath() {
       let points = [];
       const amp = 40;
       const k = 2 * Math.PI / this.wavelength;
       const omega = 0.2;
       for (let x = 0; x <= 400; x += 2) {
          // Wave packet envelope (Gaussian-ish)
          const envelope = Math.exp(-Math.pow(x - this.particleX, 2) / 4000);
          const y = 120 - amp * envelope * Math.sin(k * (x - this.particleX) - this.t * 5);
          points.push(`${x},${y.toFixed(1)}`);
       }
       return "M " + points.join(" L ");
    },
    envelopePath() {
       let points = [];
       const amp = 45;
       for (let x = 0; x <= 400; x += 5) {
          const envelope = Math.exp(-Math.pow(x - this.particleX, 2) / 4000);
          points.push(`${x},${(120 - amp * envelope).toFixed(1)}`);
       }
       for (let x = 400; x >= 0; x -= 5) {
          const envelope = Math.exp(-Math.pow(x - this.particleX, 2) / 4000);
          points.push(`${x},${(120 + amp * envelope).toFixed(1)}`);
       }
       return "M " + points.join(" L ") + " Z";
    }
  },
  mounted() { this.animate(); },
  beforeDestroy() { this.running = false; },
  methods: {
    animate() {
       if (!this.running) return;
       this.t += 0.05;
       this.particleX = (this.particleX + this.velocity * 0.1) % 400;
       requestAnimationFrame(this.animate);
    }
  }
}
</script>

<style scoped lang="scss">
.de-broglie-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }

.viz-header-compact {
  display: flex; justify-content: space-between; align-items: center; padding: 0 4px;
  .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; }
  .formula-chip { background: #fdf2f8; color: #9d174d; border: 1px solid #fbcfe8; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; }
}

.simulation-stage {
  flex: 1; min-height: 240px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden;
  .hud-panel {
    position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10;
    .hud-item {
      background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px;
      label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; }
      .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; }
      &.cyan { border-left: 3px solid #06b6d4; }
      &.orange { border-left: 3px solid #f43f5e; }
    }
  }
  .wave-info-tag { position: absolute; bottom: 12px; right: 12px; font-size: 11px; font-weight: bold; background: rgba(0,0,0,0.6); padding: 4px 10px; border-radius: 4px; border: 1px solid currentColor; }
}

.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.flex { display: flex; } .flex-col { flex-direction: column; } .flex-1 { flex: 1; } .gap-2 { gap: 8px; } .justify-between { justify-content: space-between; } .items-center { align-items: center; }
.val-tag { background: #f1f5f9; color: #475569; padding: 2px 8px; border-radius: 4px; font-size: 10px; font-weight: 700; width: 60px; text-align: right; }

.discovery-card-compact {
  background: #fdf2f8; padding: 10px 14px; border-radius: 10px; border: 1px solid #fce7f3; display: flex; align-items: center; gap: 10px;
  i { color: #db2777; font-size: 1.1rem; }
  p { flex: 1; margin: 0; font-size: 12px; color: #9d174d; line-height: 1.5; strong { color: #831843; } }
}
</style>
